import { useRef, useCallback } from "react"
import useMergeState from "./useMergeState"

/**
 * 📱 useTouch
 *
 * A React hook for handling touch events with delta calculations.
 *
 * @param {Object} options - Configuration options
 * @param {Function} [options.onStart] - Callback fired when touch starts
 * @param {Function} [options.onMove] - Callback fired when touch moves, receives event and delta data
 * @param {Function} [options.onEnd] - Callback fired when touch ends, receives event and delta data
 * @param {boolean} [options.skip=false] - When true, all touch handlers are skipped
 *
 * @returns {Object} Object containing:
 * - `state`: { isTouching: boolean } - Current touch state
 * - `events`: { onTouchStart, onTouchMove, onTouchEnd } - Event handlers to spread on a component
 *
 * Example usage:
 * ```jsx
 * const { events, state } = useTouch({
 *   onStart: (e) => console.log('Touch started'),
 *   onMove: (e, { dx, dy }) => console.log(`Moved: ${dx}px, ${dy}px`),
 *   onEnd: (e, { dx, dy }) => console.log(`Total movement: ${dx}px, ${dy}px`)
 * });
 *
 * return <div {...events}>{state.isTouching ? 'Touching' : 'Not touching'}</div>;
 * ```
 */
const useTouch = ({ onStart, onMove, onEnd, skip = false }) => {
  const [state, setState] = useMergeState({
    isTouching: false
  })
  const touchStartRef = useRef(null)

  const setTouchStartRef = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    }
  }

  const touchStartHandler = useCallback(
    (e) => {
      if (skip) return
      setState({ isTouching: true })
      setTouchStartRef(e)
      onStart && onStart(e)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onStart, skip]
  )

  const touchMoveHandler = useCallback(
    (e) => {
      if (skip) return
      const data = {
        dx: e.touches[0].clientX - touchStartRef.current.x,
        dy: e.touches[0].clientY - touchStartRef.current.y
      }
      setState({ isTouching: true })
      onMove && onMove(e, data)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onMove, skip]
  )

  const touchEndHandler = useCallback(
    (e) => {
      if (skip) return
      const data = {
        dx: e.changedTouches[0].clientX - touchStartRef.current.x,
        dy: e.changedTouches[0].clientY - touchStartRef.current.y
      }
      setState({ isTouching: false })
      onEnd(e, data)
      touchStartRef.current = null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEnd, skip]
  )

  const events = Object.freeze({
    onTouchStart: touchStartHandler,
    onTouchMove: touchMoveHandler,
    onTouchEnd: touchEndHandler
  })

  return {
    state,
    events
  }
}

export default useTouch
